import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./modal.scss";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../__redux/slices/StoreSlice";
import Tooltip from "./Tooltip";
import tooltipIcon from "../assets/icons/tooltip.png";
import Button from "./Button/Button";
import CloseButton from "./CloseButton";

const Modal = ({ product, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(0); // Index of selected size
  const modalRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const dispatch = useDispatch();

  const descriptionRef = useRef(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const isMobile = useSelector((state) => state.appSlice.isMobile);

  useEffect(() => {
    const descriptionContainer = descriptionRef.current;
    if (descriptionContainer) {
      const isOverflowing =
        descriptionContainer.scrollHeight >
        parseFloat(getComputedStyle(descriptionContainer).fontSize) * 15;
      setShowScrollbar(isOverflowing);
    }
  }, [product.description]);

  // Get current size details
  const currentSize = product.availableSizes
    ? product.availableSizes[selectedSize]
    : null;
  const isCurrentSizeInStock = currentSize
    ? currentSize.inStock
    : product.inStock;
  const currentPrice = currentSize ? currentSize.price : product.price;
  const currentSizeValue = currentSize ? currentSize.size : product.size;
  const currentForm = currentSize ? currentSize.form : product.form;
  const currentRootstock = currentSize
    ? currentSize.rootstock
    : product.rootstock;
  const currentPollination = currentSize
    ? currentSize.pollination
    : product.pollination;

  const handleSizeChange = (event) => {
    setSelectedSize(parseInt(event.target.value));
  };

  const handleAddToCart = (event) => {
    event.stopPropagation();
    const productDataForCart = {
      title: product.title,
      price: currentPrice,
      imagePath: product.imagePath,
      numInCart: quantity,
      size: currentSizeValue,
      form: currentForm,
      key: Math.random() * 11,
    };

    dispatch(addToCart(productDataForCart));
    setShowOverlay(true);
    setTimeout(() => setShowOverlay(false), 2500);
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [onClose]);

  const handleImageError = (e) => {
    e.target.src = "./store-images/rootstock.png";
  };

  const handleQuantityChange = (e) => {
    const newQuantity = e.target.value;
    setQuantity(newQuantity ? parseInt(newQuantity, 10) : "");
  };

  const handleQuantityBlur = () => {
    if (!quantity || quantity < 1) {
      setQuantity(1);
    }
  };

  const incrementQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const modalContent = (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" ref={modalRef}>
        {showOverlay && (
          <div className="overlay">
            Added {quantity} {quantity === 1 ? "item" : "items"} to Cart!
          </div>
        )}
        <button
          className="close-button"
          onClick={onClose}
          aria-label="Close modal"
        >
          <CloseButton size={24} />
        </button>
        <div className="modal-left">
          <h2>{product.title}</h2>

          {/* Size selector - only show if multiple sizes available */}
          {product.availableSizes && product.availableSizes.length > 1 && (
            <div className="modal-size-selector">
              <label htmlFor="size-select">Size:</label>
              <select
                id="size-select"
                value={selectedSize}
                onChange={handleSizeChange}
                className="size-dropdown"
              >
                {product.availableSizes.map((size, index) => (
                  <option key={index} value={index}>
                    {size.size} - ${size.price}
                  </option>
                ))}
              </select>
            </div>
          )}

          <p className="price">${currentPrice}</p>
          <p className={isCurrentSizeInStock ? "available" : "unavailable"}>
            {isCurrentSizeInStock ? "In Stock" : "Currently Unavailable"}
          </p>
          <div className="mobile-product-img">
            {isMobile && (
              <img
                src={product.imagePath}
                onError={handleImageError}
                alt={product.title}
              />
            )}
          </div>
          <div
            className={`product-description-container ${
              showScrollbar ? "scrollable" : ""
            }`}
            ref={descriptionRef}
          >
            <p>{product.description}</p>
          </div>
          <div>
            <div className="quantity-input-container">
              <div className="quantity-controls">
                <button
                  type="button"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                  className="quantity-btn"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={handleQuantityChange}
                  onBlur={handleQuantityBlur}
                  min="1"
                  aria-label="Product quantity"
                />
                <button
                  type="button"
                  onClick={incrementQuantity}
                  className="quantity-btn"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <Button
                disabled={!isCurrentSizeInStock}
                onClick={handleAddToCart}
                className="add-to-cart-btn"
                variant="secondary"
              >
                Add to Cart
              </Button>
            </div>
          </div>
        </div>
        <div className="modal-right">
          {!isMobile && (
            <img
              src={product.imagePath}
              onError={handleImageError}
              alt={product.title}
              loading="lazy"
            />
          )}
          <div className="product-detail-container">
            {currentSizeValue && (
              <Tooltip text="The sizes listed in cm are heights. Those in mm are the width of the trunk.">
                <div className="product-detail">
                  <img
                    className="__tooltip"
                    alt="tooltip icon"
                    src={tooltipIcon}
                  />
                  <span className="bold">Size: </span>
                  <span>{currentSizeValue}</span>
                </div>
              </Tooltip>
            )}

            {currentForm && (
              <Tooltip text="Many of the items are being sold bare root. meaning that there is no soil or pot with the plant and that it must be planted immediately or stored appropriately.">
                <div className="product-detail">
                  <img
                    className="__tooltip"
                    alt="tooltip icon"
                    src={tooltipIcon}
                  />
                  <span className="bold">Form: </span>
                  <span>{currentForm}</span>
                </div>
              </Tooltip>
            )}
            {currentRootstock && (
              <Tooltip text="Rootstock refers to the lower part of a grafted plant, determining the tree's growth rate, size, and resilience. It affects the fruit yield, disease resistance, and adaptability to soil and climate conditions.">
                <div className="product-detail">
                  <img
                    className="__tooltip"
                    alt="tooltip icon"
                    src={tooltipIcon}
                  />
                  <span className="bold">Rootstock: </span>
                  <span>{currentRootstock}</span>
                </div>
              </Tooltip>
            )}
            {currentPollination && (
              <Tooltip text="Proper pollination is essential for some plants to produce fruit. Make sure you understand the pollination requirements of your purchase.">
                <div className="product-detail">
                  <img
                    className="__tooltip"
                    alt="tooltip icon"
                    src={tooltipIcon}
                  />
                  <span className="bold">Pollination: </span>
                  <span>{currentPollination}</span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
